import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";
import {
  Package,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Box,
  Layers,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGING_TYPES = {
  box: { label: "Caixa", icon: "📦" },
  pallet: { label: "Palete", icon: "🏗️" },
  bag: { label: "Saco", icon: "👜" },
  container: { label: "Contentor", icon: "🗃️" },
};

export default function Packaging() {
  const [loading, setLoading] = useState(true);
  const [packagingTypes, setPackagingTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    packaging_type: "box",
    capacity: "",
    dimensions: "",
    weight_kg: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/packaging-types`);
      setPackagingTypes(res.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      packaging_type: "box",
      capacity: "",
      dimensions: "",
      weight_kg: "",
      notes: "",
    });
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      capacity: formData.capacity ? parseInt(formData.capacity) : 0,
      weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : 0,
    };

    try {
      if (editingItem) {
        await axios.put(`${API}/packaging-types/${editingItem.id}`, payload);
        toast.success("Tipo de embalagem atualizado!");
      } else {
        await axios.post(`${API}/packaging-types`, payload);
        toast.success("Tipo de embalagem criado!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      showError(error, "Erro ao guardar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code,
      description: item.description,
      packaging_type: item.packaging_type,
      capacity: item.capacity?.toString() || "",
      dimensions: item.dimensions || "",
      weight_kg: item.weight_kg?.toString() || "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este tipo de embalagem?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/packaging-types/${id}`);
      toast.success("Tipo de embalagem eliminado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="packaging-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Gestão de Embalagens
          </h1>
          <p className="text-muted-foreground mt-1">
            Tipos de embalagem para ordens de produção
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="add-packaging-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Embalagem
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{packagingTypes.length}</p>
                <p className="text-xs text-muted-foreground">Total Tipos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Box className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {packagingTypes.filter(p => p.packaging_type === 'box').length}
                </p>
                <p className="text-xs text-muted-foreground">Caixas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Layers className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {packagingTypes.filter(p => p.packaging_type === 'pallet').length}
                </p>
                <p className="text-xs text-muted-foreground">Paletes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {packagingTypes.filter(p => p.packaging_type === 'container').length}
                </p>
                <p className="text-xs text-muted-foreground">Contentores</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <Package className="h-5 w-5" />
            Tipos de Embalagem
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Capacidade</TableHead>
                <TableHead>Dimensões</TableHead>
                <TableHead>Peso (kg)</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packagingTypes.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono font-bold">{item.code}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {PACKAGING_TYPES[item.packaging_type]?.icon} {PACKAGING_TYPES[item.packaging_type]?.label || item.packaging_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono">{item.capacity || '-'}</TableCell>
                  <TableCell>{item.dimensions || '-'}</TableCell>
                  <TableCell className="font-mono">{item.weight_kg || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate">{item.notes || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        data-testid={`edit-${item.code}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-${item.code}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {packagingTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Nenhum tipo de embalagem definido. Clique em "Adicionar Embalagem" para criar.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-chivo">
              {editingItem ? "Editar Embalagem" : "Adicionar Embalagem"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Ex: CX-P"
                  required
                  data-testid="packaging-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select
                  value={formData.packaging_type}
                  onValueChange={(v) => setFormData({ ...formData, packaging_type: v })}
                >
                  <SelectTrigger data-testid="packaging-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="box">📦 Caixa</SelectItem>
                    <SelectItem value="pallet">🏗️ Palete</SelectItem>
                    <SelectItem value="bag">👜 Saco</SelectItem>
                    <SelectItem value="container">🗃️ Contentor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ex: Caixa Pequena"
                required
                data-testid="packaging-description"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Capacidade (unidades)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="100"
                  data-testid="packaging-capacity"
                />
              </div>
              <div className="space-y-2">
                <Label>Dimensões</Label>
                <Input
                  value={formData.dimensions}
                  onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                  placeholder="30x20x15 cm"
                  data-testid="packaging-dimensions"
                />
              </div>
              <div className="space-y-2">
                <Label>Peso (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.weight_kg}
                  onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                  placeholder="0.5"
                  data-testid="packaging-weight"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Observações adicionais..."
                data-testid="packaging-notes"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-packaging">
                {editingItem ? "Atualizar" : "Criar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
